import type { StoredEvent } from "@facade/dashboard-shared";
import { JsonView } from "./JsonView";
import { NodeTree } from "./NodeTree";
import { Shot } from "./Shot";
import { shotRef } from "../lib/format";

/** Kind-aware expansion for one event; falls back to raw JSON for every unknown kind. */
export function EventDetail({ e, onOpenQuery }: { e: StoredEvent; onOpenQuery?: (e: StoredEvent) => void }) {
  const p = e.payload as any;
  switch (e.kind) {
    case "screen.observe":
      return (
        <div className="stack">
          {p?.summary && <JsonView value={p.summary} />}
          {p?.listing && (
            <details>
              <summary className="muted">listing shown to the model</summary>
              <pre className="mono">{p.listing}</pre>
            </details>
          )}
          {p?.nodes?.blob ? <NodeTree sha={p.nodes.blob} /> : <div className="muted">no node tree blob</div>}
          {p?.sampleIds && (
            <details>
              <summary className="muted">sampleIds ({p.sampleIds.length})</summary>
              <pre className="mono">{p.sampleIds.join("\n")}</pre>
            </details>
          )}
        </div>
      );
    case "screen.shot": {
      const ref = shotRef(p);
      return ref ? (
        <div className="row">
          <Shot sha={ref.sha} />
          <JsonView value={p} />
        </div>
      ) : (
        <JsonView value={p} />
      );
    }
    case "model.query":
      return (
        <div className="stack">
          <div className="row">
            <span className={`badge ${p?.ok === false ? "fail" : "ok"}`}>{p?.ok === false ? "error" : "ok"}</span>
            <span className="mono">{p?.model}</span>
            <span className="muted">{p?.provider}</span>
            {p?.usage && (
              <span className="muted">
                in {p.usage.input ?? 0} · out {p.usage.output ?? 0}
                {p.usage.cacheRead ? ` · cache ${p.usage.cacheRead}` : ""}
              </span>
            )}
            {p?.latencyMs != null && <span className="muted">{p.latencyMs}ms</span>}
            {onOpenQuery && (
              <button className="small primary" onClick={() => onOpenQuery(e)}>
                open query
              </button>
            )}
          </div>
          {p?.error && <div style={{ color: "var(--bad)" }}>{p.error}</div>}
          <JsonView value={{ request: p?.request, response: p?.response }} />
        </div>
      );
    case "graph.snapshot":
      return (
        <div className="row">
          <JsonView value={p} />
          {p?.blob && (
            <a href={`/api/blobs/${p.blob}`} target="_blank" rel="noreferrer">
              download graph.json
            </a>
          )}
        </div>
      );
    default:
      return <JsonView value={p} />;
  }
}
